import { Col, Input, Row, Table, TablePaginationConfig, TableProps } from 'antd';
import { SortDirectionDto } from 'Api/Features/General/Dtos/SortDirectionDto';
import Icon from 'Components/icons/icon';
import debounce from 'lodash.debounce';
import { autorun } from 'mobx';
import { TABLE_DEBOUNCE_DELAY } from 'Models/Constants';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './index.less';
import ChannelSelect from 'Components/channel-select';
import { ArticleChannelDto } from 'Api/Features/Articles/Dtos/ArticleChannelDto';

export const initialPaginationState: TablePaginationConfig = {
    current: 1,
    pageSize: 10,
    position: ['bottomRight', 'topRight'],
    showSizeChanger: true,
};

interface BaseTableProps extends TableProps<any> {
    fetch: (params: any) => Promise<void>;
    paginationRef: TablePaginationConfig;
    searchPlaceholder: string;
}

export const BaseTable: FunctionComponent<BaseTableProps> = ({
    fetch,
    paginationRef,
    searchPlaceholder,
    columns,
    dataSource,
    loading,
    pagination,
}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedChannel, setSelectedChannel] = useState<ArticleChannelDto>();
    let navigate = useNavigate();
    const { t } = useTranslation();

    const debouncedSearch = useRef(
        debounce((searchValue: string) => {
            setSearchTerm(searchValue);
        }, TABLE_DEBOUNCE_DELAY)
    ).current;

    const debounceSearchChange = useRef(
        debounce((params: { searchTerm?: string; channel?: ArticleChannelDto }) => {
            fetch({
                pagination: {
                    ...paginationRef,
                    current: 1,
                },
                searchTerm: params.searchTerm,
                channel: params.channel,
                sortColumn: null,
                sortDirection: null,
            });
        }, TABLE_DEBOUNCE_DELAY)
    ).current;

    const handleOnRow = (row: any) => ({
        onClick: (): void => {
            navigate(row.id);
        },
    });

    const handleTableChange = async (
        pagination: TablePaginationConfig,
        filter: any,
        sorter: any
    ): Promise<void> => {
        let sortDirection: SortDirectionDto | null;
        switch (sorter.order) {
            case 'ascend':
                sortDirection = SortDirectionDto.Ascending;
                break;
            case 'descend':
                sortDirection = SortDirectionDto.Descending;
                break;
            default:
                sortDirection = null;
                break;
        }

        await fetch({
            pagination,
            searchTerm,
            sortColumn: sorter.columnKey,
            sortDirection: sortDirection,
        });

        paginationRef = pagination;
    };

    useEffect(() => {
        const disposer = autorun(() => {
            debounceSearchChange({ searchTerm, channel: selectedChannel });
        });
        return (): void => {
            disposer();
            debouncedSearch.cancel();
            debounceSearchChange.cancel();
        };
    }, [searchTerm, selectedChannel, debounceSearchChange, debouncedSearch]);

    return (
        <Row className="BaseTable">
            <Col span={24}>
                <Row>
                    <Col offset={7} span={8}>
                        <ChannelSelect
                            onChange={(value: ArticleChannelDto): void => {
                                setSelectedChannel(value);
                                debounceSearchChange({ searchTerm, channel: value });
                            }}
                            includeAllOption={false}
                            allowClear
                        />
                    </Col>
                    <Col offset={1} span={8}>
                        <Input
                            className="BaseTable__search"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                debouncedSearch(e.target.value)
                            }
                            prefix={<Icon iconName="MagnifyingGlass" />}
                            placeholder={searchPlaceholder}
                        />
                    </Col>
                </Row>
                <Col span={24}>
                    <Table
                        className="ant-table-action-row"
                        locale={{
                            emptyText: (
                                <div className="BaseTable__empty">{t('table_no_results')}</div>
                            ),
                        }}
                        columns={columns}
                        dataSource={dataSource}
                        onChange={handleTableChange}
                        onRow={handleOnRow}
                        loading={loading}
                        pagination={pagination}
                        showSorterTooltip={false}
                        bordered={true}
                    />
                </Col>
            </Col>
        </Row>
    );
};
